import { type FC, useState, useCallback, type ReactElement } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { ImageEditModal } from 'dibs-media-uploader';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import Pen from 'dibs-icons/exports/legacy/Pen.ts';

import { InlineMediaUploaderTooltipMenu } from './InlineMediaUploaderTooltipMenu.tsx';
import { type ProcessImagePayload } from '../../services/cropService.ts';

import styles from './InlineMediaUploader.scss';

type Props = {
    src: string | null | undefined;
    alt: string;
    placeholder: ReactElement;
    onDrop: (files: Array<File>) => Promise<void>;
    onDelete: () => Promise<void>;
    onCrop: () => void;
    onCropComplete: (cropPayload: ProcessImagePayload & { photoId: number }) => Promise<void>;
    onModalClose: () => void;
    imageClass?: string;
    containerClass?: string;
    isRoundImage?: boolean;
    imageRequirements: {
        cropShape: 'round' | 'rect';
        targetWidth: number;
        targetHeight: number;
    };
    imageToCrop: {
        path: string;
        id: string | number;
    } | null;
};

export const InlineMediaUploader: FC<Props> = ({
    src,
    alt,
    placeholder,
    imageClass,
    containerClass,
    onModalClose,
    onDrop,
    onDelete,
    onCrop,
    onCropComplete,
    isRoundImage,
    imageRequirements,
    imageToCrop,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);

    const handleDrop = async (files: Array<File>): Promise<void> => {
        setTooltipVisible(false);
        setIsLoading(true);
        await onDrop(files);
        setIsLoading(false);
    };

    const {
        getInputProps,
        getRootProps,
        open: openFileDialog,
    } = useDropzone({
        multiple: false,
        accept: 'image/*',
        onDrop: handleDrop,
    });

    const handleDelete = async (): Promise<void> => {
        setTooltipVisible(false);
        setIsLoading(true);
        await onDelete();
        setIsLoading(false);
    };

    const handleCropComplete = async (data: ProcessImagePayload): Promise<void> => {
        if (imageToCrop?.id) {
            setIsLoading(true);
            await onCropComplete?.({
                ...data,
                photoId: +imageToCrop?.id,
            });
            setIsLoading(false);
        }
    };

    const handleReplace = (): void => {
        openFileDialog();
    };

    const onCloseTooltipMenu = useCallback(() => setTooltipVisible(false), []);

    let content;
    if (isLoading) {
        content = (
            <div className={containerClass}>
                <div
                    className={classnames(styles.loadingOverlay, {
                        [styles.roundOverlay]: isRoundImage,
                    })}
                >
                    <Spinner size="small" black />
                </div>
                {src ? <img src={src} alt={alt} className={imageClass} /> : placeholder}
            </div>
        );
    } else if (!src) {
        content = (
            <div {...getRootProps()} className={containerClass} role="button" tabIndex={0}>
                {placeholder}
                <input {...getInputProps()} />
            </div>
        );
    } else {
        content = (
            <div
                className={containerClass}
                onClick={() => setTooltipVisible(true)}
                onKeyDown={() => setTooltipVisible(true)}
                role="button"
                tabIndex={0}
            >
                <div
                    className={classnames(styles.overlay, {
                        [styles.forceHover]: tooltipVisible,
                        [styles.roundOverlay]: isRoundImage,
                    })}
                >
                    <div
                        className={classnames(styles.tooltipContainer, {
                            [styles.center]: isRoundImage,
                        })}
                    >
                        <Pen
                            className={classnames(styles.icon, { [styles.noMargin]: isRoundImage })}
                        />
                        <InlineMediaUploaderTooltipMenu
                            isVisible={tooltipVisible}
                            onClose={onCloseTooltipMenu}
                            onDelete={handleDelete}
                            onCrop={onCrop}
                            onReplace={handleReplace}
                        />
                    </div>
                </div>
                <img src={src} alt={alt} className={imageClass} />
                <input {...getInputProps()} />
            </div>
        );
    }

    return (
        <>
            {content}
            <ImageEditModal
                isOpen={imageToCrop}
                onClose={onModalClose}
                onSave={handleCropComplete}
                onDelete={onDelete}
                imageUrl={imageToCrop?.path}
                imageReqs={{
                    ...imageRequirements,
                    aspect: imageRequirements.targetWidth / imageRequirements.targetHeight,
                }}
                customStyles={styles.imageModalCustomStyles}
                title={
                    <FormattedMessage
                        id="nft.publicProfile.mediaUploader.modal.title"
                        defaultMessage="Crop Image"
                    />
                }
                subTitle={
                    <FormattedMessage
                        id="nft.publicProfile.mediaUploader.modal.subTitle"
                        defaultMessage="Drag the image to adjust the crop, and use the slider to adjust the zoom of the image."
                    />
                }
                saveCtaText={
                    <FormattedMessage
                        id="nft.publicProfile.mediaUploader.modal.saveImage"
                        defaultMessage="Save Image"
                    />
                }
            />
        </>
    );
};
